import React from "react";
import ReactDOM from "react-dom/client";
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { Provider } from "react-redux";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { PersistGate } from "redux-persist/integration/react";
import authReducer, { setLogout } from "./state";
import { apiSlice } from "./redux/apiSlice";
import api from "./state/axios";
import App from "./App";
import "./index.css";

const rootReducer = combineReducers({
  auth: authReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
});

const persistConfig = {
  key: "root",
  storage,
  version: 1,
  whitelist: ["auth"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(apiSlice.middleware),
  devTools: false,
});

setupListeners(store.dispatch);

api.interceptors.request.use((config) => {
  const token = store.getState().auth?.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Only logout on 401 when online — offline 401s are network failures,
// not invalid sessions, so we must not clear credentials.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && navigator.onLine) {
      store.dispatch(setLogout());
    }
    return Promise.reject(err);
  }
);

const persistor = persistStore(store);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <App />
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
